import { useCallback, useEffect, useMemo, useState } from 'react';
import { computeItemCharge } from '@forq/rental-data';
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { AdminPage, createDemoAdminGateway } from '@forq/admin';
import { createDemoImportGateway, ImportPage } from '@forq/import';
import { createDemoDocumentArchiveGateway, createDemoRecordDeletionGateway } from '@forq/platform-data';
import type { DeletableEntity } from '@forq/contracts';
import { CompletePreviewPage, PreviewModulePage, previewModuleDefinitions } from '@forq/complete-preview';
import { createDemoCustomersGateway, CustomersPage, type CustomerFinancials, type CustomerInvoiceLine, type CustomerOperation } from '@forq/customers';
import { AuswertungPage } from '@forq/auswertung';
import { createDemoMastersGateway } from '@forq/masters-data';
import { createDemoMachinesGateway, MachinesPage, type MachineOperation } from '@forq/machines';
import { createDemoMasterdataGateway, MasterdataPage, type ArticleMovement, type ArticleStock, type ArticleStockLocation, type ArticleSupplierLink } from '@forq/masterdata';
import { createDemoOrdersGateway } from '@forq/orders';
import { createDemoPartsGateway, PartsPage, UebungsshopPage } from '@forq/parts';
import { usePlatform } from '@forq/platform';
import { createInMemoryAttachmentDocumentGateway, createLocalBugReportGateway, createLocalCompanyProfileGateway, createLocalUiLayoutGateway } from '@forq/platform-data';
import { createDemoSalesGateway } from '@forq/sales';
import { createDemoServiceGateway } from '@forq/service';
import { AppShell, CustomizeProvider, EmptyState } from '@forq/ui';
import { createDemoWarehouseGateway, WarehousePage } from '@forq/warehouse';
import { AppBootstrap } from './bootstrap/AppBootstrap';
import { usePreviewPersistence } from './bootstrap/PreviewPersistenceContext';
import { createSupabaseRuntimeServices, resolveSupabaseBootstrapConfiguration } from './bootstrap/runtime';
import { runtime } from './runtime';
import { CommandPalette } from './CommandPalette';
import { AbschlussPage } from './finance/AbschlussPage';
import { BuchhaltungPage } from './finance/BuchhaltungPage';
import { CockpitPage } from './finance/CockpitPage';

import { FinancePage } from './finance/FinancePage';
import { AnkaufPage } from './finance/AnkaufPage';

import { IncomingInvoicesPage } from './finance/incoming/IncomingInvoicesPage';
import { MahnwesenPage } from './finance/MahnwesenPage';
import { OposPage } from './finance/OposPage';
import {
  createDemoExportLogGateway,
  createDemoFinanceGateway,
  createDemoIncomingInvoiceGateway,
  createDemoPeriodClosingGateway,
  daysOverdue,
  invoiceSign,
  isBookedInvoice,
  openAmount,
} from '@forq/finance-data';
import { createDemoRentalGateway } from '@forq/rental-data';
import { RentalPage } from './rental/RentalPage';
import { ForqyDock } from './forqy/ForqyDock';
import { BugReportDialog } from './bugs/BugReportDialog';
import { createDemoSignalGateway } from './signal/demoSignalGateway';
import { SignalCenter } from './signal/SignalCenter';
import { describeDue, todayIsoDate } from './signal/types';
import { useSignalTasks } from './signal/useSignalTasks';
import { partsServerConfigured } from './partsServer';
import { GlobalContextMenu } from './GlobalContextMenu';
import { StartPage } from './start/StartPage';
import type { DashboardMetrics } from './start/dashboardProfiles';
import { FinanceServiceBillingPage } from './workspaces/FinanceServiceBillingPage';
import { OrdersProfessionalWorkspace } from './workspaces/OrdersProfessionalWorkspace';
import { SalesCenterWorkspace } from './workspaces/SalesCenterWorkspace';
import { ServiceBillingWorkspace } from './workspaces/ServiceBillingWorkspace';

const demoAdminGateway = createDemoAdminGateway();
// Im Demo-Modus kennt die Vorschau keine echten Verweise: dort haengt nie etwas dran, es wird
// also immer geloescht. Die REGEL ist dieselbe -- nur die Datenlage ist eine andere.
//
// Wichtig ist die Rueckmeldung an die Modul-Gateways: ohne sie meldet der Demo-Modus "geloescht",
// waehrend der Eintrag in der Liste stehen bleibt.
const demoDeletionGateway = createDemoRecordDeletionGateway({
  onDeleted: (entity, id) => entfernenAusDemo(entity, id),
  onArchived: (entity, id) => entfernenAusDemo(entity, id),
});

function entfernenAusDemo(entity: DeletableEntity, id: string): void {
  // tenantId kennt der Aufruf nicht -- die Demo-Gateways halten je Mandant eine Liste, also
  // wird in allen gesucht. Die IDs sind mandantenweit eindeutig.
  for (const tenantId of ['ftw', 'stw', 'gsl']) {
    switch (entity) {
      case 'customer': demoCustomersGateway.removeCustomer(tenantId, id); break;
      case 'machine': demoMachinesGateway.removeMachine(tenantId, id); break;
      case 'article': demoMasterdataGateway.removeItem(tenantId, id); break;
      case 'supplier': demoMasterdataGateway.removeSupplier(tenantId, id); break;
      case 'offer': demoSalesGateway.removeOffer(tenantId, id); break;
      case 'order': demoOrdersGateway.removeOrder(tenantId, id); break;
      case 'warehouse-location': demoWarehouseGateway.removeLocation(tenantId, id); break;
    }
  }
}
// Die Demo-Übernahme kennt dieselben Maschinentypen wie der Demo-Maschinenstamm -- sonst
// zeigt die Zuordnungstabelle Typen an, die es nirgends gibt.
const demoImportGateway = createDemoImportGateway([
  { id: 'forklift', name: 'Gabelstapler' },
  { id: 'reach-truck', name: 'Schubmaststapler' },
  { id: 'pallet-truck', name: 'Niederhubwagen' },
  { id: 'stacker', name: 'Hochhubwagen' },
  { id: 'order-picker', name: 'Kommissionierer' },
  { id: 'sweeper', name: 'Kehrmaschine' },
  { id: 'scrubber', name: 'Scheuersaugmaschine' },
  { id: 'aerial-platform', name: 'Arbeitsbühne' },
  { id: 'other', name: 'Sonstige Maschine' },
]);
const demoCustomersGateway = createDemoCustomersGateway();
const demoMachinesGateway = createDemoMachinesGateway();
const demoMasterdataGateway = createDemoMasterdataGateway();
const demoOrdersGateway = createDemoOrdersGateway();
const demoSalesGateway = createDemoSalesGateway();
const demoServiceGateway = createDemoServiceGateway();
const demoFinanceGateway = createDemoFinanceGateway();
const demoPeriodClosingGateway = createDemoPeriodClosingGateway();
const demoExportLogGateway = createDemoExportLogGateway();
const demoCompanyProfileGateway = createLocalCompanyProfileGateway();
const demoUiLayoutGateway = createLocalUiLayoutGateway();
// Belegarchiv ohne Datenbank: nur im Arbeitsspeicher -- eine Aufbewahrungspflicht laesst sich im
// Browser nicht erfuellen, und ein localStorage-Archiv wuerde genau das vortaeuschen.
const demoDocumentArchiveGateway = createDemoDocumentArchiveGateway();
const demoBugReportGateway = createLocalBugReportGateway();
const demoAttachmentDocumentGateway = createInMemoryAttachmentDocumentGateway();
const demoIncomingInvoiceGateway = createDemoIncomingInvoiceGateway();
const demoRentalGateway = createDemoRentalGateway();
const demoSignalGateway = createDemoSignalGateway();
const demoWarehouseGateway = createDemoWarehouseGateway();
// Der Uebungslieferant arbeitet im Demo-Betrieb vollstaendig -- deshalb haengt der Parts-Gateway
// an den Demo-Gateways von Vorgang und Lager: Bestandsabgleich und Wareneingang sollen echt sein.
const demoPartsGateway = createDemoPartsGateway({ orders: demoOrdersGateway, warehouse: demoWarehouseGateway });
const demoMastersGateway = createDemoMastersGateway();

function AppRoutes() {
  const platform = usePlatform();
  const previewPersistence = usePreviewPersistence();
  const modules = runtime.modules.listVisible(platform.permissions);
  const enabledFeatureIds = runtime.features.listVisible(platform.permissions).filter((feature) => platform.isFeatureEnabled(feature.id)).map((feature) => feature.id);
  const runtimeServices = useMemo(() => {
    const configuration = resolveSupabaseBootstrapConfiguration(import.meta.env);
    return configuration.kind === 'supabase' ? createSupabaseRuntimeServices(configuration.config) : null;
  }, []);
  const adminGateway = runtimeServices?.admin ?? demoAdminGateway;
  const importGateway = runtimeServices?.import ?? demoImportGateway;
  const deletionGateway = runtimeServices?.deletion ?? demoDeletionGateway;
  const customersGateway = runtimeServices?.customers ?? demoCustomersGateway;
  const machinesGateway = runtimeServices?.machines ?? demoMachinesGateway;
  const masterdataGateway = runtimeServices?.masterdata ?? demoMasterdataGateway;
  const ordersGateway = runtimeServices?.orders ?? demoOrdersGateway;
  const salesGateway = runtimeServices?.sales ?? demoSalesGateway;
  const serviceGateway = runtimeServices?.service ?? demoServiceGateway;
  const serviceBillingGateway = previewPersistence.billingGateway;
  const rentalGateway = runtimeServices?.rental ?? demoRentalGateway;
  // Behebt zugleich den alten Bug "App.tsx verdrahtet im Supabase-Modus hart den Demo-Finance-Gateway".
  const financeGateway = runtimeServices?.finance ?? demoFinanceGateway;
  const incomingGateway = runtimeServices?.incoming ?? demoIncomingInvoiceGateway;
  const companyProfileGateway = runtimeServices?.companyProfile ?? demoCompanyProfileGateway;
  const uiLayoutGateway = runtimeServices?.uiLayout ?? demoUiLayoutGateway;
  const documentArchiveGateway = runtimeServices?.documentArchive ?? demoDocumentArchiveGateway;
  const bugReportGateway = runtimeServices?.bugReports ?? demoBugReportGateway;
  const attachmentDocumentGateway = runtimeServices?.attachmentDocuments ?? demoAttachmentDocumentGateway;
  const exportLogGateway = runtimeServices?.exportLog ?? demoExportLogGateway;
  const closingGateway = runtimeServices?.periodClosing ?? demoPeriodClosingGateway;
  const mastersGateway = runtimeServices?.masters ?? demoMastersGateway;

  // Per-machine live financials: Mieterträge aus dem Mietpark + Werkstattkosten aus den Service-
  // Abrechnungen (Join Proposal.serviceJobId -> ServiceJob.machineNumber), keyed by Maschinennummer.
  const loadMachineFinancials = useCallback(async (tenantId: string) => {
    const [rentalOrders, servicePage, proposals] = await Promise.all([
      rentalGateway.listRentalOrders(tenantId),
      serviceGateway.searchJobs({ tenantId, pageSize: 500 }),
      serviceBillingGateway.listProposals(tenantId, 'all'),
    ]);
    const map: Record<string, { workshopCosts: number; rentalRevenue: number }> = {};
    const ensure = (number: string) => {
      const existing = map[number];
      if (existing) return existing;
      const created = { workshopCosts: 0, rentalRevenue: 0 };
      map[number] = created;
      return created;
    };
    for (const order of rentalOrders) {
      for (const item of order.items) {
        if (item.machineNumber) ensure(item.machineNumber).rentalRevenue += computeItemCharge(item, item.startDate, item.actualReturnDate ?? item.endDate);
      }
    }
    const jobMachine = new Map<string, string>();
    for (const job of servicePage.items) jobMachine.set(job.id, job.machineNumber);
    for (const proposal of proposals) {
      const machineNumber = jobMachine.get(proposal.serviceJobId);
      if (machineNumber) ensure(machineNumber).workshopCosts += proposal.netTotal;
    }
    return map;
  }, [rentalGateway, serviceGateway, serviceBillingGateway]);
  // Verknüpfte Vorgänge je Maschine (Miete/Service/Auftrag) für den Vorgänge-Tab, keyed by Maschinennummer.
  const loadMachineOperations = useCallback(async (tenantId: string) => {
    const [rentalOrders, servicePage, orderPage] = await Promise.all([
      rentalGateway.listRentalOrders(tenantId),
      serviceGateway.searchJobs({ tenantId, pageSize: 500 }),
      ordersGateway.searchOrders({ tenantId, pageSize: 500 }),
    ]);
    const map: Record<string, MachineOperation[]> = {};
    const push = (number: string, op: MachineOperation) => { (map[number] ??= []).push(op); };
    for (const order of rentalOrders) {
      for (const item of order.items) {
        if (item.machineNumber) push(item.machineNumber, { kind: 'rental', number: order.number, title: order.customerName || 'Mietauftrag', status: order.status, date: order.startDate || order.createdAt.slice(0, 10) });
      }
    }
    for (const job of servicePage.items) {
      if (job.machineNumber) push(job.machineNumber, { kind: 'service', number: job.serviceNumber, title: job.title, status: job.status, date: job.createdAt.slice(0, 10) });
    }
    for (const order of orderPage.items) {
      if (order.machineNumber) push(order.machineNumber, { kind: 'order', number: order.number, title: order.title, status: order.status, date: order.createdAt.slice(0, 10) });
    }
    for (const number of Object.keys(map)) map[number].sort((left, right) => right.date.localeCompare(left.date));
    return map;
  }, [rentalGateway, serviceGateway, ordersGateway]);
  // Verknüpfte Vorgänge je Kunde (Auftrag/Service/Miete/Angebot) für den Vorgänge-Tab der Kundenakte,
  // keyed by Kundennummer.
  const loadCustomerOperations = useCallback(async (tenantId: string) => {
    const [orderPage, servicePage, rentalOrders, offerPage] = await Promise.all([
      ordersGateway.searchOrders({ tenantId, pageSize: 500 }),
      serviceGateway.searchJobs({ tenantId, pageSize: 500 }),
      rentalGateway.listRentalOrders(tenantId),
      salesGateway.searchOffers({ tenantId, pageSize: 500 }),
    ]);
    const map: Record<string, CustomerOperation[]> = {};
    const push = (number: string, op: CustomerOperation) => { if (number) (map[number] ??= []).push(op); };
    for (const order of orderPage.items) {
      push(order.customerNumber, { kind: 'order', number: order.number, title: order.title, status: order.status, date: order.createdAt.slice(0, 10) });
    }
    for (const job of servicePage.items) {
      push(job.customerNumber, { kind: 'service', number: job.serviceNumber, title: job.title, status: job.status, date: job.createdAt.slice(0, 10) });
    }
    for (const order of rentalOrders) {
      push(order.customerNumber, { kind: 'rental', number: order.number, title: 'Mietauftrag', status: order.status, date: order.startDate || order.createdAt.slice(0, 10) });
    }
    for (const offer of offerPage.items) {
      push(offer.customerNumber, { kind: 'offer', number: offer.number, title: offer.title, status: offer.status, date: offer.createdAt.slice(0, 10) });
    }
    for (const number of Object.keys(map)) map[number].sort((left, right) => right.date.localeCompare(left.date));
    return map;
  }, [ordersGateway, serviceGateway, rentalGateway, salesGateway]);
  // Kennzahlen je Kunde für die Kundenakte: gebuchter Netto-Umsatz (Gutschriften abgezogen), offene
  // Posten (offene/teilbezahlte Rechnungen) und eine Rechnungsliste, keyed by Kundennummer.
  const loadCustomerFinancials = useCallback(async (tenantId: string) => {
    const invoices = await financeGateway.listInvoices({ tenantId, documentStatus: 'all' });
    const map: Record<string, CustomerFinancials & { _revenue: number; _open: number; _lines: CustomerInvoiceLine[] }> = {};
    const ensure = (number: string) => {
      const existing = map[number];
      if (existing) return existing;
      const created = { revenue: 0, openReceivables: 0, invoices: [], _revenue: 0, _open: 0, _lines: [] as CustomerInvoiceLine[] };
      map[number] = created;
      return created;
    };
    for (const invoice of invoices) {
      if (!invoice.customerNumber) continue;
      const bucket = ensure(invoice.customerNumber);
      if (isBookedInvoice(invoice)) bucket._revenue += invoice.netTotal * invoiceSign(invoice);
      const open = invoice.paymentStatus === 'open' || invoice.paymentStatus === 'partial' ? openAmount(invoice) : 0;
      bucket._open += open;
      bucket._lines.push({
        number: invoice.invoiceNumber ?? invoice.proposalNumber,
        date: invoice.issueDate ?? invoice.createdAt.slice(0, 10),
        gross: invoice.grossTotal,
        open,
        status: invoice.paymentStatus,
      });
    }
    const result: Record<string, CustomerFinancials> = {};
    for (const [number, bucket] of Object.entries(map)) {
      bucket._lines.sort((left, right) => right.date.localeCompare(left.date));
      result[number] = {
        revenue: Math.round(bucket._revenue * 100) / 100,
        openReceivables: Math.round(bucket._open * 100) / 100,
        invoices: bucket._lines,
      };
    }
    return result;
  }, [financeGateway]);
  const warehouseGateway = runtimeServices?.warehouse ?? demoWarehouseGateway;
  const partsGateway = runtimeServices?.parts ?? demoPartsGateway;
  // Im Demo-Betrieb spielt der Uebungslieferant den ganzen Ablauf durch. Mit echter Datenbank
  // braucht es den Bestelldienst des FORQ-Servers -- ohne ihn bleiben Suche und Absenden gesperrt.
  const partsOrderingAvailable = runtimeServices ? partsServerConfigured() : true;
  // Lagerstand + Bewegungen je Artikel für die Artikelakte. WICHTIG: Lager und Stamm haben getrennte
  // Artikel-IDs -- verknüpft wird über die Artikelnummer (itemNumber), nie über die id.
  const loadArticleStock = useCallback(async (tenantId: string) => {
    const [stockPage, movements] = await Promise.all([
      warehouseGateway.searchStock({ tenantId, filter: 'all', pageSize: 1000 }),
      warehouseGateway.listMovements(tenantId, 250),
    ]);
    const map: Record<string, { onHand: number; reserved: number; available: number; lowStock: boolean; locations: ArticleStockLocation[]; movements: ArticleMovement[] }> = {};
    const ensure = (number: string) => (map[number] ??= { onHand: 0, reserved: 0, available: 0, lowStock: false, locations: [], movements: [] });
    for (const row of stockPage.items) {
      if (!row.itemNumber) continue;
      const bucket = ensure(row.itemNumber);
      bucket.onHand += row.onHand;
      bucket.reserved += row.reserved;
      bucket.available += row.available;
      if (row.lowStock) bucket.lowStock = true;
      bucket.locations.push({ locationCode: row.locationCode, locationName: row.locationName, onHand: row.onHand, available: row.available, minimumStock: row.minimumStock, lowStock: row.lowStock });
    }
    for (const movement of movements) {
      if (!movement.itemNumber) continue;
      ensure(movement.itemNumber).movements.push({ type: movement.type, quantity: movement.quantity, from: movement.sourceLocationCode, to: movement.targetLocationCode, reason: movement.reason, reference: movement.reference, date: movement.occurredAt });
    }
    const result: Record<string, ArticleStock> = {};
    for (const [number, bucket] of Object.entries(map)) {
      bucket.movements.sort((left, right) => right.date.localeCompare(left.date));
      bucket.locations.sort((left, right) => left.locationCode.localeCompare(right.locationCode));
      result[number] = {
        onHand: Math.round(bucket.onHand * 1000) / 1000,
        reserved: Math.round(bucket.reserved * 1000) / 1000,
        available: Math.round(bucket.available * 1000) / 1000,
        lowStock: bucket.lowStock,
        locations: bucket.locations,
        movements: bucket.movements,
      };
    }
    return result;
  }, [warehouseGateway]);
  // Lieferanten je Artikel (aus den Lieferanten-Verknüpfungen), keyed by Artikelnummer. searchSuppliers
  // liefert die items[] je Lieferant mit -- daraus die Rückrichtung „welche Lieferanten führen Artikel X".
  const loadArticleSuppliers = useCallback(async (tenantId: string) => {
    const suppliers = await masterdataGateway.searchSuppliers({ tenantId, pageSize: 1000 });
    const map: Record<string, ArticleSupplierLink[]> = {};
    for (const supplier of suppliers.items) {
      for (const link of supplier.items) {
        if (!link.itemNumber) continue;
        (map[link.itemNumber] ??= []).push({ supplierNumber: supplier.number, supplierName: supplier.name, supplierItemNumber: link.supplierItemNumber, purchasePrice: link.purchasePrice, leadTimeDays: link.leadTimeDays, preferred: link.preferred });
      }
    }
    for (const number of Object.keys(map)) {
      map[number].sort((left, right) => Number(right.preferred) - Number(left.preferred) || left.supplierName.localeCompare(right.supplierName));
    }
    return map;
  }, [masterdataGateway]);
  // Jeder Stammdaten-Einstieg der Seitenleiste ist bewusst fokussiert: /artikel zeigt nur Artikel,
  // /dienstleistungen nur Leistungen, /lieferanten nur Lieferanten -- daher je Route nur die eine
  // Bereichs-Feature-Id durchreichen (der In-Page-Bereichswechsler blendet sich dann aus).
  const articlesFeatureIds = enabledFeatureIds.filter((id) => id === 'masterdata.articles');
  const servicesFeatureIds = enabledFeatureIds.filter((id) => id === 'masterdata.services');
  const suppliersFeatureIds = enabledFeatureIds.filter((id) => id === 'masterdata.suppliers');

  const [commandOpen, setCommandOpen] = useState(false);
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setCommandOpen((open) => !open);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Mandanten-Akzentfarbe auf die Oberfläche legen: die (schon für die Belege genutzte) accentColor
  // des aktiven Mandanten wird zur UI-Akzentfarbe -- so erkennt man sofort, in welchem FORQ man
  // arbeitet. --forq-accent-strong wird aus ihr abgeleitet. Aktualisiert beim Mandantenwechsel und
  // live, wenn die Farbe in der Grundkonfiguration geändert wird (Custom-Event).
  useEffect(() => {
    let active = true;
    const apply = (color: string) => {
      const accent = /^#[0-9a-fA-F]{6}$/.test(color) ? color : '#E5232F';
      const root = document.documentElement;
      root.style.setProperty('--forq-accent', accent);
      root.style.setProperty('--forq-accent-strong', `color-mix(in srgb, ${accent} 80%, white)`);
    };
    const reload = () => { void companyProfileGateway.loadProfile(platform.activeTenant.id).then((profile) => { if (active) apply(profile.letterhead.accentColor); }).catch(() => {}); };
    reload();
    window.addEventListener('forq:company-profile-saved', reload);
    return () => { active = false; window.removeEventListener('forq:company-profile-saved', reload); };
  }, [companyProfileGateway, platform.activeTenant.id]);

  const navigate = useNavigate();
  const [signalOpen, setSignalOpen] = useState(false);
  const [forqyOpen, setForqyOpen] = useState(false);
  const [bugOpen, setBugOpen] = useState(false);
  const signal = useSignalTasks(demoSignalGateway, platform.activeTenant.id, platform.permissions);
  const signalToday = todayIsoDate();
  const dashboardTasks = signal.tasks.map((task) => {
    const due = describeDue(task.dueAt, signalToday);
    return { id: task.id, title: task.title, meta: `${task.sourceModule} · ${due.label}`, tone: due.tone, route: task.route };
  });

  // Live-Kennzahlen fürs Dashboard: nur laden, was der User darf; fehlende Felder bleiben „–".
  const [dashboardMetrics, setDashboardMetrics] = useState<DashboardMetrics>({});
  useEffect(() => {
    let active = true;
    const tenantId = platform.activeTenant.id;
    const has = (permission: string) => platform.permissions.has(permission);
    const next: { -readonly [K in keyof DashboardMetrics]: DashboardMetrics[K] } = {};
    const jobs: Promise<void>[] = [];
    if (has('service.view')) {
      jobs.push(serviceGateway.getOverview(tenantId).then((overview) => { next.service = overview; }).catch(() => {}));
      jobs.push(serviceGateway.listUvvDue(tenantId).then((list) => { next.uvvDue = list.length; }).catch(() => {}));
    }
    if (has('sales.view')) {
      jobs.push(salesGateway.getOverview(tenantId).then((overview) => { next.sales = overview; }).catch(() => {}));
    }
    if (has('finance.view')) {
      jobs.push(financeGateway.listInvoices({ tenantId, documentStatus: 'all' }).then((invoices) => {
        const open = invoices.filter((invoice) => invoice.paymentStatus === 'open' || invoice.paymentStatus === 'partial');
        next.financeOpen = open.length;
        next.financeOverdue = open.filter((invoice) => daysOverdue(invoice, signalToday) > 0).length;
        next.financeOpenAmount = Math.round(open.reduce((sum, invoice) => sum + openAmount(invoice), 0) * 100) / 100;
      }).catch(() => {}));
    }
    if (has('rental.view')) {
      jobs.push(rentalGateway.listRentalOrders(tenantId).then((list) => {
        next.rentalActive = list.filter((order) => order.status === 'active').length;
        next.rentalRequests = list.filter((order) => order.status === 'request').length;
        next.rentalReturnDue = list.filter((order) => order.status === 'return_scheduled').length;
        next.rentalBillingReady = list.filter((order) => order.billingStatus === 'ready').length;
      }).catch(() => {}));
    }
    if (has('warehouse.view')) {
      jobs.push(warehouseGateway.getOverview(tenantId).then((overview) => {
        next.warehouseStocked = overview.stockedItems;
        next.warehouseLow = overview.lowStockItems;
        next.warehouseNegative = overview.negativeStockItems;
        next.warehouseReplenish = overview.openReplenishmentItems;
      }).catch(() => {}));
    }
    void Promise.all(jobs).then(() => { if (active) setDashboardMetrics(next); });
    return () => { active = false; };
  }, [platform.activeTenant.id, platform.permissions, serviceGateway, salesGateway, financeGateway, rentalGateway, warehouseGateway, signalToday]);

  return (
   <CustomizeProvider tenantId={platform.activeTenant.id} gateway={uiLayoutGateway}>
    <AppShell
      modules={modules}
      onOpenCommand={() => setCommandOpen(true)}
      onOpenSignal={() => setSignalOpen(true)}
      signalCount={signal.tasks.length}
      onOpenForqy={() => setForqyOpen(true)}
      onReportBug={() => setBugOpen(true)}
    >
      <Routes>
        <Route path="/start" element={<Navigate to="/dashboard" replace />} />
        <Route path="/complete-preview" element={<CompletePreviewPage tenant={platform.activeTenant} mode={platform.mode} />} />
        <Route path="/dashboard" element={<StartPage userName={platform.user.displayName} permissions={platform.permissions} tasks={dashboardTasks} onOpenCommand={() => setCommandOpen(true)} onNavigate={(route) => navigate(route)} tenantId={platform.activeTenant.id} canCustomize={platform.permissions.has('admin.view')} layoutGateway={uiLayoutGateway} metrics={dashboardMetrics} />} />
        <Route path="/auswertung" element={<AuswertungPage tenant={platform.activeTenant} mode={platform.mode} permissions={platform.permissions} enabledFeatureIds={enabledFeatureIds} salesGateway={salesGateway} financeGateway={financeGateway} incomingGateway={incomingGateway} warehouseGateway={warehouseGateway} serviceGateway={serviceGateway} rentalGateway={rentalGateway} machinesGateway={machinesGateway} />} />
        <Route path="/auswertung/:section" element={<AuswertungPage tenant={platform.activeTenant} mode={platform.mode} permissions={platform.permissions} enabledFeatureIds={enabledFeatureIds} salesGateway={salesGateway} financeGateway={financeGateway} incomingGateway={incomingGateway} warehouseGateway={warehouseGateway} serviceGateway={serviceGateway} rentalGateway={rentalGateway} machinesGateway={machinesGateway} />} />
        <Route path="/vorgaenge" element={<OrdersProfessionalWorkspace tenant={platform.activeTenant} mode={platform.mode} permissions={platform.permissions} enabledFeatureIds={enabledFeatureIds} gateway={ordersGateway} deletionGateway={deletionGateway} companyGateway={companyProfileGateway} attachmentDocumentGateway={attachmentDocumentGateway} financeGateway={financeGateway} machinesGateway={machinesGateway} masterdataGateway={masterdataGateway} mastersGateway={mastersGateway} customersGateway={customersGateway} partsGateway={partsGateway} partsOrderingAvailable={partsOrderingAvailable} />} />
        <Route path="/vorgaenge/:orderNumber" element={<OrdersProfessionalWorkspace tenant={platform.activeTenant} mode={platform.mode} permissions={platform.permissions} enabledFeatureIds={enabledFeatureIds} gateway={ordersGateway} deletionGateway={deletionGateway} companyGateway={companyProfileGateway} attachmentDocumentGateway={attachmentDocumentGateway} financeGateway={financeGateway} machinesGateway={machinesGateway} masterdataGateway={masterdataGateway} mastersGateway={mastersGateway} customersGateway={customersGateway} partsGateway={partsGateway} partsOrderingAvailable={partsOrderingAvailable} />} />
        <Route path="/orders" element={<Navigate to="/vorgaenge" replace />} />
        <Route path="/service" element={<ServiceBillingWorkspace tenant={platform.activeTenant} mode={platform.mode} permissions={platform.permissions} enabledFeatureIds={enabledFeatureIds} serviceGateway={serviceGateway} ordersGateway={ordersGateway} billingGateway={serviceBillingGateway} companyGateway={companyProfileGateway} preparedBy={platform.user.displayName} />} />
        <Route path="/finance" element={<CockpitPage tenant={platform.activeTenant} mode={platform.mode} permissions={platform.permissions} financeGateway={financeGateway} incomingGateway={incomingGateway} ordersGateway={ordersGateway} />} />
        <Route path="/finance/abrechnung" element={<FinancePage tenant={platform.activeTenant} mode={platform.mode} permissions={platform.permissions} financeGateway={financeGateway} ordersGateway={ordersGateway} customersGateway={customersGateway} companyGateway={companyProfileGateway} mastersGateway={mastersGateway} machinesGateway={machinesGateway} masterdataGateway={masterdataGateway} archiveGateway={documentArchiveGateway} />} />
        <Route path="/finance/rechnungen/:nummer" element={<FinancePage tenant={platform.activeTenant} mode={platform.mode} permissions={platform.permissions} financeGateway={financeGateway} ordersGateway={ordersGateway} customersGateway={customersGateway} companyGateway={companyProfileGateway} mastersGateway={mastersGateway} machinesGateway={machinesGateway} masterdataGateway={masterdataGateway} archiveGateway={documentArchiveGateway} />} />
        <Route path="/finance/ankauf" element={<AnkaufPage tenant={platform.activeTenant} mode={platform.mode} permissions={platform.permissions} machinesGateway={machinesGateway} masterdataGateway={masterdataGateway} companyGateway={companyProfileGateway} />} />
        <Route path="/finance/eingangsrechnungen" element={<IncomingInvoicesPage tenant={platform.activeTenant} mode={platform.mode} permissions={platform.permissions} gateway={incomingGateway} />} />
        <Route path="/finance/offene-posten" element={<OposPage tenant={platform.activeTenant} mode={platform.mode} permissions={platform.permissions} financeGateway={financeGateway} incomingGateway={incomingGateway} customersGateway={customersGateway} masterdataGateway={masterdataGateway} />} />
        <Route path="/finance/mahnwesen" element={<MahnwesenPage tenant={platform.activeTenant} mode={platform.mode} permissions={platform.permissions} financeGateway={financeGateway} companyGateway={companyProfileGateway} customersGateway={customersGateway} />} />
        <Route path="/finance/auswertungen" element={<Navigate to="/auswertung/finanzen" replace />} />
        <Route path="/finance/buchhaltung" element={<BuchhaltungPage tenant={platform.activeTenant} mode={platform.mode} permissions={platform.permissions} userName={platform.user.displayName} financeGateway={financeGateway} incomingGateway={incomingGateway} exportLogGateway={exportLogGateway} companyGateway={companyProfileGateway} />} />
        <Route path="/finance/abschluss" element={<AbschlussPage tenant={platform.activeTenant} mode={platform.mode} permissions={platform.permissions} userName={platform.user.displayName} financeGateway={financeGateway} incomingGateway={incomingGateway} ordersGateway={ordersGateway} closingGateway={closingGateway} />} />
        <Route path="/mietpark" element={<RentalPage tenant={platform.activeTenant} mode={platform.mode} permissions={platform.permissions} gateway={rentalGateway} financeGateway={financeGateway} customersGateway={customersGateway} companyGateway={companyProfileGateway} machinesGateway={machinesGateway} userName={platform.user.displayName} />} />
        <Route path="/finance/service-vorschlaege" element={<FinanceServiceBillingPage tenant={platform.activeTenant} gateway={serviceBillingGateway} financeGateway={financeGateway} companyGateway={companyProfileGateway} masterdataGateway={masterdataGateway} serviceGateway={serviceGateway} machinesGateway={machinesGateway} />} />
        <Route path="/kunden" element={<CustomersPage tenant={platform.activeTenant} mode={platform.mode} permissions={platform.permissions} enabledFeatureIds={enabledFeatureIds} gateway={customersGateway} machinesGateway={machinesGateway} deletionGateway={deletionGateway} companyGateway={companyProfileGateway} loadOperations={loadCustomerOperations} loadFinancials={loadCustomerFinancials} />} />
        <Route path="/kunden/:kundennummer" element={<CustomersPage tenant={platform.activeTenant} mode={platform.mode} permissions={platform.permissions} enabledFeatureIds={enabledFeatureIds} gateway={customersGateway} machinesGateway={machinesGateway} deletionGateway={deletionGateway} companyGateway={companyProfileGateway} loadOperations={loadCustomerOperations} loadFinancials={loadCustomerFinancials} />} />
        <Route path="/customers" element={<Navigate to="/kunden" replace />} />
        <Route path="/maschinen" element={<MachinesPage tenant={platform.activeTenant} mode={platform.mode} permissions={platform.permissions} enabledFeatureIds={enabledFeatureIds} gateway={machinesGateway} loadFinancials={loadMachineFinancials} loadOperations={loadMachineOperations} deletionGateway={deletionGateway} companyGateway={companyProfileGateway} mastersGateway={mastersGateway} />} />
        <Route path="/maschinen/:maschinennummer" element={<MachinesPage tenant={platform.activeTenant} mode={platform.mode} permissions={platform.permissions} enabledFeatureIds={enabledFeatureIds} gateway={machinesGateway} loadFinancials={loadMachineFinancials} loadOperations={loadMachineOperations} deletionGateway={deletionGateway} companyGateway={companyProfileGateway} mastersGateway={mastersGateway} />} />
        <Route path="/machines" element={<Navigate to="/maschinen" replace />} />
        <Route path="/artikel" element={<MasterdataPage tenant={platform.activeTenant} mode={platform.mode} permissions={platform.permissions} enabledFeatureIds={articlesFeatureIds} gateway={masterdataGateway} deletionGateway={deletionGateway} companyGateway={companyProfileGateway} loadStock={loadArticleStock} loadSuppliers={loadArticleSuppliers} mastersGateway={mastersGateway} />} />
        <Route path="/artikel/:nummer" element={<MasterdataPage tenant={platform.activeTenant} mode={platform.mode} permissions={platform.permissions} enabledFeatureIds={articlesFeatureIds} gateway={masterdataGateway} deletionGateway={deletionGateway} companyGateway={companyProfileGateway} loadStock={loadArticleStock} loadSuppliers={loadArticleSuppliers} mastersGateway={mastersGateway} />} />
        <Route path="/dienstleistungen" element={<MasterdataPage tenant={platform.activeTenant} mode={platform.mode} permissions={platform.permissions} enabledFeatureIds={servicesFeatureIds} gateway={masterdataGateway} deletionGateway={deletionGateway} companyGateway={companyProfileGateway} loadStock={loadArticleStock} loadSuppliers={loadArticleSuppliers} mastersGateway={mastersGateway} />} />
        <Route path="/dienstleistungen/:nummer" element={<MasterdataPage tenant={platform.activeTenant} mode={platform.mode} permissions={platform.permissions} enabledFeatureIds={servicesFeatureIds} gateway={masterdataGateway} deletionGateway={deletionGateway} companyGateway={companyProfileGateway} loadStock={loadArticleStock} loadSuppliers={loadArticleSuppliers} mastersGateway={mastersGateway} />} />
        <Route path="/lieferanten" element={<MasterdataPage tenant={platform.activeTenant} mode={platform.mode} permissions={platform.permissions} enabledFeatureIds={suppliersFeatureIds} gateway={masterdataGateway} deletionGateway={deletionGateway} companyGateway={companyProfileGateway} loadStock={loadArticleStock} loadSuppliers={loadArticleSuppliers} mastersGateway={mastersGateway} />} />
        <Route path="/lieferanten/:nummer" element={<MasterdataPage tenant={platform.activeTenant} mode={platform.mode} permissions={platform.permissions} enabledFeatureIds={suppliersFeatureIds} gateway={masterdataGateway} deletionGateway={deletionGateway} companyGateway={companyProfileGateway} loadStock={loadArticleStock} loadSuppliers={loadArticleSuppliers} mastersGateway={mastersGateway} />} />
        <Route path="/masterdata" element={<Navigate to="/artikel" replace />} />
        <Route path="/services" element={<Navigate to="/dienstleistungen" replace />} />
        <Route path="/suppliers" element={<Navigate to="/lieferanten" replace />} />
        <Route path="/lager" element={<WarehousePage tenant={platform.activeTenant} mode={platform.mode} permissions={platform.permissions} enabledFeatureIds={enabledFeatureIds} gateway={warehouseGateway} deletionGateway={deletionGateway} />} />
        <Route path="/lager/:section" element={<WarehousePage tenant={platform.activeTenant} mode={platform.mode} permissions={platform.permissions} enabledFeatureIds={enabledFeatureIds} gateway={warehouseGateway} deletionGateway={deletionGateway} />} />
        <Route path="/warehouse" element={<Navigate to="/lager" replace />} />
        {/* Der Übungsshop steht VOR der Modulseite: sonst würde /ersatzteile/uebungsshop von der
            Modulseite geschluckt. Er spielt den Lieferanten und bestellt selbst nichts. */}
        <Route path="/ersatzteile/uebungsshop" element={<UebungsshopPage tenant={platform.activeTenant} gateway={partsGateway} />} />
        <Route path="/ersatzteile" element={<PartsPage tenant={platform.activeTenant} mode={platform.mode} permissions={platform.permissions} enabledFeatureIds={enabledFeatureIds} gateway={partsGateway} orderingAvailable={partsOrderingAvailable} />} />
        <Route path="/parts" element={<Navigate to="/ersatzteile" replace />} />
        <Route path="/verkauf" element={<SalesCenterWorkspace tenant={platform.activeTenant} mode={platform.mode} permissions={platform.permissions} enabledFeatureIds={enabledFeatureIds} gateway={salesGateway} deletionGateway={deletionGateway} customersGateway={customersGateway} financeGateway={financeGateway} companyGateway={companyProfileGateway} machinesGateway={machinesGateway} masterdataGateway={masterdataGateway} mastersGateway={mastersGateway} />} />
        <Route path="/verkauf/:view" element={<SalesCenterWorkspace tenant={platform.activeTenant} mode={platform.mode} permissions={platform.permissions} enabledFeatureIds={enabledFeatureIds} gateway={salesGateway} deletionGateway={deletionGateway} customersGateway={customersGateway} financeGateway={financeGateway} companyGateway={companyProfileGateway} machinesGateway={machinesGateway} masterdataGateway={masterdataGateway} mastersGateway={mastersGateway} />} />
        <Route path="/sales" element={<Navigate to="/verkauf" replace />} />
        {previewModuleDefinitions.filter((definition) => !['orders', 'service', 'finance', 'rental', 'logistics', 'technician'].includes(definition.id)).map((definition) => (
          <Route key={definition.id} path={definition.route} element={<PreviewModulePage moduleId={definition.id} tenant={platform.activeTenant} mode={platform.mode} />} />
        ))}
        <Route path="/workshop" element={<Navigate to="/service" replace />} />
        {/* Monteur-Modul entfernt -- alte Einstiege leiten aufs Dashboard. */}
        <Route path="/monteur" element={<Navigate to="/dashboard" replace />} />
        <Route path="/time-tracking" element={<Navigate to="/dashboard" replace />} />
        <Route path="/technician" element={<Navigate to="/dashboard" replace />} />
        <Route path="/rental" element={<Navigate to="/mietpark" replace />} />
        <Route path="/purchasing" element={<Navigate to="/einkauf" replace />} />
        <Route path="/admin" element={<AdminPage tenant={platform.activeTenant} mode={platform.mode} permissions={platform.permissions} enabledFeatureIds={enabledFeatureIds} gateway={adminGateway} machinesGateway={machinesGateway} mastersGateway={mastersGateway} companyGateway={companyProfileGateway} attachmentDocumentGateway={attachmentDocumentGateway} bugGateway={bugReportGateway} />} />
        <Route path="/datenimport" element={<ImportPage tenant={platform.activeTenant} gateway={importGateway} />} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<EmptyState eyebrow="Noch nicht vorhanden" title="Dieser Arbeitsbereich ist noch nicht registriert." description="FORQ lädt nur Module, die einen gültigen Vertrag besitzen." />} />
      </Routes>
      <CommandPalette
        open={commandOpen}
        onClose={() => setCommandOpen(false)}
        tenantId={platform.activeTenant.id}
        permissions={platform.permissions}
        gateways={{ customers: customersGateway, machines: machinesGateway, orders: ordersGateway }}
      />
      <SignalCenter
        open={signalOpen}
        onClose={() => setSignalOpen(false)}
        tenantName={platform.activeTenant.name}
        tasks={signal.tasks}
        loading={signal.loading}
        completing={signal.completing}
        onOpenRoute={(route) => { setSignalOpen(false); navigate(route); }}
        onComplete={signal.complete}
      />
      <ForqyDock open={forqyOpen} onClose={() => setForqyOpen(false)} />
      <BugReportDialog open={bugOpen} tenantId={platform.activeTenant.id} gateway={bugReportGateway} onClose={() => setBugOpen(false)} />
      <GlobalContextMenu onSearch={() => setCommandOpen(true)} onHome={() => navigate('/dashboard')} onBack={() => navigate(-1)} />
    </AppShell>
   </CustomizeProvider>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <AppBootstrap>
        <AppRoutes />
      </AppBootstrap>
    </BrowserRouter>
  );
}
